// Static catalog of classes offered by Rhythm Check Training.
// Promoted equally across the site.

export const CLASSES = [
  {
    slug: "bls",
    code: "BLS",
    name: "Basic Life Support (BLS)",
    tagline: "AHA-aligned CPR & AED training for healthcare providers.",
    audience: "Nurses, medical assistants, EMTs, hospital and clinic staff.",
    duration: "4 hours",
    format: "In-person, hands-on skills evaluation.",
    certification: "2-year provider card issued upon successful completion.",
    price: 50,
    highlights: [
      "High-quality CPR for adults, children, and infants",
      "AED use and team dynamics",
      "Bag-mask ventilation and choking relief",
      "Skills testing with certified instructor"
    ],
    outcomes: [
      "Recognize sudden cardiac arrest and initiate the chain of survival",
      "Deliver effective chest compressions and ventilations",
      "Use an AED safely on adult and pediatric patients"
    ],
    imageKey: "hero_cpr"
  },
  {
    slug: "acls",
    code: "ACLS",
    name: "Advanced Cardiovascular Life Support (ACLS)",
    tagline: "For healthcare professionals leading resuscitation teams.",
    audience: "Physicians, RNs, paramedics, respiratory therapists.",
    duration: "1–2 days (initial); 4–5 hours (renewal)",
    format: "In-person megacode scenarios and rhythm interpretation.",
    certification: "2-year AHA-aligned ACLS provider card.",
    price: 150,
    variants: [
      { label: "Initial", price: 250 },
      { label: "Renewal", price: 150 }
    ],
    highlights: [
      "ECG rhythm recognition & pharmacology",
      "Cardiac arrest algorithms (VF/pVT, PEA/asystole)",
      "Acute coronary syndromes & stroke algorithms",
      "Team leadership in megacode simulations"
    ],
    outcomes: [
      "Lead a resuscitation team using ACLS algorithms",
      "Interpret core arrhythmias and act decisively",
      "Manage peri-arrest conditions confidently"
    ],
    imageKey: "medical_team"
  },
  {
    slug: "pals",
    code: "PALS",
    name: "Pediatric Advanced Life Support (PALS)",
    tagline: "Advanced pediatric resuscitation for clinical teams.",
    audience: "Pediatric ICU/ED staff, transport teams, EMS professionals.",
    duration: "1–2 days (initial); 4–5 hours (renewal)",
    format: "In-person case-based scenarios & skills stations.",
    certification: "2-year AHA-aligned PALS provider card.",
    price: 185,
    variants: [
      { label: "Initial", price: 285 },
      { label: "Renewal", price: 185 }
    ],
    highlights: [
      "Systematic pediatric assessment",
      "Recognition and management of shock and respiratory distress",
      "PALS cardiac arrest algorithms",
      "Team communication in high-stakes scenarios"
    ],
    outcomes: [
      "Systematically assess a seriously ill child",
      "Recognize and treat respiratory failure and shock early",
      "Apply PALS algorithms in cardiac arrest"
    ],
    imageKey: "medical_team"
  },
  {
    slug: "heartsaver",
    code: "HEARTSAVER",
    name: "Heartsaver CPR / AED / First Aid",
    tagline: "Layperson certification for schools, workplaces, and community.",
    audience: "Teachers, coaches, fitness staff, parents, general public.",
    duration: "3–4 hours",
    format: "In-person, no medical background required.",
    certification: "2-year Heartsaver course completion card.",
    price: 75,
    highlights: [
      "Hands-only and full CPR for adults, children, infants",
      "AED operation for laypeople",
      "First aid essentials: bleeding, burns, shock, choking",
      "Confidence for real-world emergencies"
    ],
    outcomes: [
      "Recognize an emergency and activate EMS",
      "Perform CPR & use an AED on victims of all ages",
      "Provide first aid until professional help arrives"
    ],
    imageKey: "hero_cpr"
  },
  {
    slug: "workplace-safety",
    code: "SAFETY",
    name: "Workplace Safety Training",
    tagline: "OSHA-aligned safety programs for teams and organizations.",
    audience: "Offices, warehouses, construction, hospitality, manufacturing.",
    duration: "Custom — 2 to 8 hours depending on scope",
    format: "On-site at your workplace or at our DFW training space.",
    certification: "Certificate of completion for each attendee.",
    price: 0,
    priceNote: "Custom group pricing — request a quote",
    highlights: [
      "Bloodborne pathogens & PPE",
      "Emergency response and evacuation planning",
      "Slips, trips, falls & ergonomics",
      "Custom modules tailored to your industry"
    ],
    outcomes: [
      "Reduce workplace incidents through prevention",
      "Meet common OSHA training expectations",
      "Empower staff to respond to emergencies confidently"
    ],
    imageKey: "workplace_training"
  }
];

export const IMAGES = {
  logo: "https://customer-assets.emergentagent.com/job_medical-training-hub-5/artifacts/4k3ssku3_RhythmCheckLogo.jpg",
  hero_cpr: "https://images.pexels.com/photos/11655091/pexels-photo-11655091.jpeg",
  medical_team: "https://images.pexels.com/photos/28123695/pexels-photo-28123695.jpeg",
  workplace_training: "https://images.pexels.com/photos/12903168/pexels-photo-12903168.jpeg",
  testimonial_doctor: "https://images.pexels.com/photos/5214963/pexels-photo-5214963.jpeg"
};

export const CONTACT = {
  phone: "214-797-5539",
  phoneHref: "tel:+12147975539",
  email: "info@rhythmchecktraining.com",
  area: "DFW Area — Dallas / Fort Worth, Texas",
  hours: "Mon – Sat, 8:00 AM – 7:00 PM CT"
};

export const getClassBySlug = (slug) => CLASSES.find((c) => c.slug === slug);

// Short price string used on cards / list rows.
export function priceLabel(c) {
  if (c.priceNote) return c.priceNote;
  if (c.variants && c.variants.length > 0) {
    const min = Math.min(...c.variants.map((v) => v.price));
    return `From $${min}`;
  }
  if (c.price > 0) return `$${c.price}`;
  return "Custom pricing";
}
